import { formatEuro } from '../../utils/format-euro'

import type {
  ServerErrorCode,
  ServerErrorParams,
} from '@brickup/shared/constants'
import { ACCOUNT } from './account'
import { ADVISOR } from './advisor'
import { AUTH } from './auth'
import { BACKUP_ERRORS } from './backup'
import { BANK } from './bank'
import { CALCULATION } from './calculation'
import { CATALOGUE } from './catalogue'
import { COMMENTS } from './comments'
import { COMMON } from './common'
import { CONFIGURATOR } from './configurator'
import { CONFIGURATOR_CMS } from './configurator-cms'
import { COST_ENTRY } from './cost-entry'
import { DEVELOPMENT } from './development'
import { DOCUMENT_ERRORS, DOCUMENTS } from './documents'
import { EMAIL_ADMIN } from './email-admin'
import { FINANCE } from './finance'
import { HISTORY } from './history'
import { HOUSE_ELEMENT } from './house-element'
import { HOUSE_ELEMENT_TYPE } from './house-element-type'
import { MATCHING } from './matching'
import { MCP } from './mcp'
import { MEDIA } from './media'
import { PRODUCT_PROPERTY } from './product-property'
import { PROJECTS } from './projects'
import { PROPERTY } from './property'
import { ROOM_ERRORS } from './rooms'
import { USERS } from './users'
import {
  decimalPlacesNotAllowed,
  FIELD_ERRORS,
  RANGE_MIN_ABOVE_MAX,
} from './validation'

/**
 * The one place a string still selects German text at runtime: the backend answers a
 * failed request with `{ code, ...params }` and the code names the condition.
 *
 * The response crosses a service boundary, but the code does not — it is the
 * `ServerErrorCode` union both sides compile against, so a code cannot be
 * thrown without a sentence here.
 *
 * Each entry's parameter is `ServerErrorParams[code]`, so the names and types
 * are the ones the throw site actually sends — renaming `min` on the backend
 * fails the build here instead of rendering `NaN`.
 */
const SERVER_ERROR_TEXT = {
  WRONG_PASSWORD: ACCOUNT.errors.wrongPassword,

  PASSWORD_TOO_SHORT: p => AUTH.passwordTooShort(p.min),

  QUANTITY_BELOW_MIN: p => HOUSE_ELEMENT.errors.quantityBelowMin(p.min),
  QUANTITY_ABOVE_MAX: p => HOUSE_ELEMENT.errors.quantityAboveMax(p.max),
  QUANTITY_DECIMAL_PLACES_EXCEEDED: p =>
    HOUSE_ELEMENT.errors.decimalPlacesExceeded(p.count),

  FORMULA_STILL_REFERENCED: p =>
    CONFIGURATOR_CMS.formulaDeleteReferencedError(p.count),
  MEDIA_ASSET_STILL_REFERENCED: p =>
    CONFIGURATOR_CMS.mediaDeleteReferencedError(p.count),
  FORMULA_SYNTAX_ERROR: p =>
    CONFIGURATOR_CMS.formulas.headlineSyntaxError(p.detail),
  FORMULA_NAME_COLLIDES_WITH_MEASURE: p =>
    CATALOGUE.errors.formulaNameCollidesWithCode(p.name),
  MEASURE_CODE_COLLIDES_WITH_FORMULA: p =>
    CATALOGUE.errors.codeCollidesWithFormula(p.code),
  MEASURE_CODE_TAKEN: p => CATALOGUE.errors.codeTaken(p.code),
  MEASURE_CODE_STILL_REFERENCED: p =>
    CATALOGUE.errors.codeStillReferenced(
      p.code,
      p.measureIds,
      p.formulaNames,
      p.blockIds,
    ),
  MEASURE_STILL_PLACED: p => CATALOGUE.errors.measureStillPlaced(p.count),
  MEASURE_EXCLUDES_ITSELF: CATALOGUE.errors.excludesItself,
  MEASURE_EXCLUSION_NOT_FOUND: CATALOGUE.errors.exclusionNotFound,
  CALCULATION_ADMIN_ONLY: CATALOGUE.errors.calculationAdminOnly,
  SLOT_NEEDS_ONE_SOURCE: CALCULATION.errors.slotNeedsOneSource,
  SLOT_ALREADY_DETACHED: CALCULATION.errors.slotAlreadyDetached,
  SLOT_REFERENCES_NOTHING: CALCULATION.errors.slotReferencesNothing,
  SLOT_MOVE_ACROSS_OWNERS: CALCULATION.errors.slotMoveAcrossOwners,
  SLOT_FOLLOWS_THE_LIBRARY: CALCULATION.errors.slotFollowsTheLibrary,
  SLOT_BUDGET_NEEDS_A_MEASURE: CALCULATION.errors.slotBudgetNeedsAMeasure,
  SLOT_BUDGET_FOREIGN_VARIANT: CALCULATION.errors.slotBudgetForeignVariant,
  GROUP_MEMBER_NEEDS_ONE_TARGET: CALCULATION.errors.groupMemberNeedsOneTarget,
  BLOCK_STILL_REFERENCED: p => CALCULATION.errors.blockStillReferenced(p.count),
  BLOCK_GROUP_STILL_REFERENCED: p =>
    CALCULATION.errors.groupStillReferenced(p.count),
  MEASURE_HOLDS_OWN_CALCULATION: p =>
    CALCULATION.errors.measureHoldsOwnCalculation(p.count),
  SORT_NEEDS_EVERY_ROW: CALCULATION.errors.sortNeedsEveryRow,
  PRODUCT_LINK_UNREADABLE: p => CATALOGUE.product.importReadFailed(p.reason),
  PRODUCT_LINK_EXTRACTION_FAILED: CATALOGUE.product.importExtractionFailed,
  PRODUCT_NO_SOURCE_URL: CATALOGUE.product.importNoSourceUrl,
  MEDIA_URL_NOT_AN_IMAGE: MEDIA.errors.urlNotAnImage,
  MEDIA_UPLOAD_NOT_STAGED: p => MEDIA.errors.uploadNotStaged(p.maxMb),
  IMAGE_USAGE_RENDERED_BY_CODE: p => MEDIA.errors.usageRenderedByCode(p.key),
  MEASURE_ALREADY_PLACED: () => CONFIGURATOR_CMS.measureAlreadyPlacedError,
  PRODUCT_STILL_LINKED: p => CATALOGUE.errors.productStillLinked(p.count),
  PRODUCT_STILL_CHARGED: p => CATALOGUE.errors.productStillCharged(p.count),
  LABOUR_ITEM_STILL_CHARGED: p =>
    CALCULATION.errors.labourItemStillCharged(p.count),
  FORMULA_NAME_TAKEN: p => CATALOGUE.errors.formulaNameTaken(p.name),
  CATALOGUE_FOLDER_NOT_EMPTY: p => CATALOGUE.errors.folderNotEmpty(p.count),
  CATALOGUE_FOLDER_KIND_MISMATCH: () => CATALOGUE.errors.folderKindMismatch,
  CATALOGUE_KIND_HAS_NO_FOLDERS: () => CATALOGUE.errors.kindHasNoFolders,
  CLASSIFICATION_KIND_MISMATCH: () =>
    CATALOGUE.errors.classificationKindMismatch,
  CATALOGUE_FOLDER_NAME_TAKEN: p => CATALOGUE.errors.folderNameTaken(p.name),
  CHANGE_BUNDLE_NOT_REVERTED: p => HISTORY.errors.bundleNotReverted(p.count),
  FORMULA_CIRCULAR_REFERENCE: p =>
    CONFIGURATOR_CMS.formulaCircularError(p.cycle),
  FORMULA_PROTECTED: p => CONFIGURATOR_CMS.formulaProtectedError(p.name),
  QUESTION_STILL_PLACED: p =>
    CONFIGURATOR_CMS.questionStillPlacedError(p.count),
  QUESTION_SEVERAL_DEFAULTS: p =>
    CONFIGURATOR_CMS.questionSeveralDefaultsError(p.count),
  CLASSIFICATION_STILL_USED: p =>
    CATALOGUE.classification.stillUsedError(p.count),
  CLASSIFICATION_NAME_TAKEN: p => CATALOGUE.classification.nameTaken(p.name),
  PRODUCT_PROPERTY_KEY_TAKEN: p => PRODUCT_PROPERTY.errors.keyTaken(p.key),
  PRODUCT_PROPERTY_STILL_USED: p => PRODUCT_PROPERTY.errors.stillUsed(p.count),
  PRODUCT_PROPERTY_OPTION_STILL_USED: p =>
    PRODUCT_PROPERTY.errors.optionStillUsed(p.count),
  PRODUCT_PROPERTY_ALREADY_ATTACHED: PRODUCT_PROPERTY.errors.alreadyAttached,
  PRODUCT_GROUP_OFFERS_NOTHING: p =>
    PRODUCT_PROPERTY.errors.offersNothing(p.label),
  PRODUCT_PROPERTY_NOT_IN_GROUPS: PRODUCT_PROPERTY.errors.notInGroups,
  REQUIREMENT_VARIANT_NOT_FOUND: PRODUCT_PROPERTY.errors.variantNotFound,
  VARIANT_PRICE_NEEDS_UNIT: PRODUCT_PROPERTY.errors.priceNeedsUnit,
  PRODUCT_PROPERTY_VALUE_WRONG_TYPE: p =>
    PRODUCT_PROPERTY.errors.wrongType(p.label),
  PRODUCT_PROPERTY_VALUE_BELOW_MINIMUM: p =>
    PRODUCT_PROPERTY.errors.belowMinimum(p.label, p.min),
  PRODUCT_PROPERTY_VALUE_ABOVE_MAXIMUM: p =>
    PRODUCT_PROPERTY.errors.aboveMaximum(p.label, p.max),
  PRODUCT_PROPERTY_OPTION_NOT_OFFERED: p =>
    PRODUCT_PROPERTY.errors.optionNotOffered(p.label),
  PRODUCT_PROPERTY_OPTION_UNKNOWN: p =>
    PRODUCT_PROPERTY.errors.optionUnknown(p.label),
  MIGRATION_NEEDS_VALUE: PRODUCT_PROPERTY.legacy.needsValue,
  MIGRATION_AXIS_TAKEN: PRODUCT_PROPERTY.legacy.axisTaken,
  SUGGESTION_ALREADY_DECIDED: PRODUCT_PROPERTY.errors.suggestionDecided,
  SUGGESTION_DECISION_UNKNOWN: PRODUCT_PROPERTY.errors.decisionUnknown,
  PRODUCT_NAME_REQUIRED: () => CATALOGUE.errors.nameRequired,
  PRODUCT_IMPORT_DRAFT_MALFORMED: () => CATALOGUE.product.importDraftMalformed,
  MEASURE_IN_CONFIGURATOR: CATALOGUE.errors.measureInConfigurator,
  MEASURE_NOT_AVAILABLE_AS_POSITION:
    CATALOGUE.errors.measureNotAvailableAsPosition,
  MEASURE_RULE_BLOCKS_POSITION: ({ title }) =>
    CATALOGUE.errors.measureRuleBlocksPosition(title),
  MEASURE_EXCLUDED_BY_POSITION: ({ title, excludedBy }) =>
    COST_ENTRY.excludedByRefused(title, excludedBy),
  POSITION_OVERRIDE_ADMIN_ONLY: COST_ENTRY.override.adminOnly,

  CHANGE_REVERT_BLOCKED: p => HISTORY.errors.revertBlocked(p.reason),
  CHANGE_REVERT_CONFLICT: p => HISTORY.errors.revertConflict(p.count),
  CHANGE_BUNDLE_NOT_FOUND: HISTORY.errors.bundleNotFound,

  CATALOGUE_KIND_UNKNOWN: CATALOGUE.errors.kindUnknown,
  MCP_CONSENT_REFUSED: MCP.consentRefused,
  MCP_UPLOAD_LINK_INVALID: MCP.upload.linkInvalid,

  MANDATE_CLOSED_IN_HUBSPOT: MATCHING.errors.mandateClosedInHubSpot,
  MANDATE_STAGE_UNKNOWN: MATCHING.errors.mandateStageUnknown,
  MANDATE_STAGE_WRITE_FAILED: MATCHING.errors.mandateStageWriteFailed,

  PROJECT_HAS_NO_CUSTOMER: PROJECTS.notifyCustomer.errors.noCustomer,
  PROJECT_NOT_ASSIGNED: PROJECTS.notAssigned.refused,
  PROJECT_UNASSIGNED: PROJECTS.notAssigned.unassignedHint,
  PROJECT_CONTACT_PERSON_LOCKED: PROJECTS.notAssigned.contactPersonLocked,
  PROJECT_COLLABORATOR_NOT_SALES: PROJECTS.access.errors.notSales,
  PROJECT_COLLABORATOR_IS_RESPONSIBLE: PROJECTS.access.errors.isResponsible,
  PROJECT_RESPONSIBLE_ONLY: PROJECTS.access.errors.responsibleOnly,
  ROLES_EMPTY: USERS.errors.rolesEmpty,
  ROLE_NOT_COMBINABLE: USERS.errors.roleNotCombinable,
  PROJECT_CUSTOMER_CHANGED: PROJECTS.notifyCustomer.errors.customerChanged,
  PROJECT_STATE_CHANGED: PROJECTS.notifyCustomer.errors.stateChanged,
  PROJECT_PUBLIC_HAS_CUSTOMER: PROJECTS.publicCustomerConflict,
  RESET_TOKEN_INVALID: AUTH.invalidResetTokenDescription,
  LISTING_URL_NOT_HTTPS: PROPERTY.listingUrlNotHttps,
  PROPERTY_TYPE_STRANDS_SELECTIONS: p =>
    PROPERTY.typeStrandsSelections(p.count),
  EMAIL_SENDING_DISABLED: EMAIL_ADMIN.robot.sendingDisabled,
  UNSUBSCRIBE_TOKEN_INVALID: AUTH.invalidTokenDescription,

  ...FIELD_ERRORS,
  RANGE_MIN_ABOVE_MAX,
  DECIMAL_PLACES_NOT_ALLOWED: p => decimalPlacesNotAllowed(p.allowed),

  ELEMENT_TYPE_KEY_COLLIDES_WITH_BUILTIN: p =>
    HOUSE_ELEMENT_TYPE.errors.keyCollidesWithBuiltin(p.key),
  ELEMENT_TYPE_OPTION_MODE_NEEDS_OPTION:
    HOUSE_ELEMENT_TYPE.errors.optionModeNeedsOption,
  ELEMENT_TYPE_OPTIONS_ONLY_ON_OPTION_MODE:
    HOUSE_ELEMENT_TYPE.errors.optionsOnlyOnOptionMode,
  ELEMENT_TYPE_FIELD_ONLY_ON_QUANTITY_MODE: p =>
    HOUSE_ELEMENT_TYPE.errors.fieldOnlyOnQuantityMode(p.field),
  SUMMARY_VALUE_MAP_INVALID: p =>
    CONFIGURATOR_CMS.summaryValueMapInvalid(p.max, p.labelMax),
  GROUP_KIND_NEEDS_CUSTOMER_SCOPE: CONFIGURATOR_CMS.levelNeedsCustomerScope,
  GROUP_KIND_PLACEMENT: CONFIGURATOR_CMS.groupKindPlacement,
  GROUP_CYCLE: CONFIGURATOR_CMS.groupCycle,
  QUESTION_SCOPE_MISMATCH: CONFIGURATOR_CMS.questionScopeMismatch,

  MERGE_NEEDS_TWO_DOCUMENTS: DOCUMENT_ERRORS.mergeNeedsTwo,
  MERGE_TOO_MANY_DOCUMENTS: p => DOCUMENT_ERRORS.mergeTooMany(p.max),
  MERGE_TOTAL_SIZE_EXCEEDED: p =>
    DOCUMENT_ERRORS.mergeTotalSizeExceeded(p.maxMb),
  MERGE_MIME_TYPE_UNSUPPORTED: p =>
    DOCUMENT_ERRORS.mergeMimeUnsupported(p.mimeType),
  SPLIT_NEEDS_A_BUCKET: DOCUMENT_ERRORS.splitNeedsBucket,
  SPLIT_ONLY_PDF: DOCUMENT_ERRORS.splitOnlyPdf,
  SPLIT_PAGE_OUT_OF_RANGE: p =>
    DOCUMENT_ERRORS.splitPageOutOfRange(p.page, p.total),
  SPLIT_SOURCE_TOO_LARGE: p => DOCUMENT_ERRORS.splitSourceTooLarge(p.maxMb),

  DOCUMENT_MIME_TYPE_UNSUPPORTED: DOCUMENTS.uploadUnsupportedType,
  DOCUMENT_UPLOAD_TOO_LARGE: p => DOCUMENTS.uploadTooLarge(p.maxMb),
  DOCUMENT_UPLOAD_NOT_FOUND: DOCUMENT_ERRORS.uploadNotFound,
  DOCUMENT_UPLOAD_SIZE_UNKNOWN: DOCUMENT_ERRORS.uploadSizeUnknown,
  DOCUMENT_UPLOAD_KEY_INVALID: DOCUMENT_ERRORS.uploadKeyInvalid,
  DOCUMENT_UPLOAD_CHANGED: DOCUMENT_ERRORS.uploadChanged,
  DOCUMENT_IS_INVOICE: p => DOCUMENT_ERRORS.isInvoice(p.number),
  MEDIA_UPLOAD_CHANGED: () => MEDIA.errors.uploadChanged,

  SYNC_NOT_A_TARGET: DEVELOPMENT.sync.errors.notATarget,
  SYNC_ORIGIN_REFUSED: DEVELOPMENT.sync.errors.originRefused,
  SYNC_ORIGIN_UNREACHABLE: p =>
    DEVELOPMENT.sync.errors.originUnreachable(p.origin),
  SYNC_SNAPSHOT_REFUSED: DEVELOPMENT.sync.errors.snapshotRefused,
  SYNC_SOURCE_BEHIND_SCHEMA: p =>
    DEVELOPMENT.sync.errors.behindSchema(p.detail),
  SYNC_SOURCE_HAS_NO_MEDIA_BUCKET: DEVELOPMENT.sync.errors.noMediaBucket,
  JOB_ALREADY_RUNNING: p => COMMON.jobAlreadyRunning(p.triggeredBy),
  JOB_SWITCHED_OFF: COMMON.jobSwitchedOff,
  USER_ROLE_HAS_NO_ADVISOR_PROFILE: ADVISOR.noProfileForRole,
  USER_LAST_NAME_REQUIRED: USERS.errors.lastNameRequired,
  USER_ORGANISATION_NOT_ALLOWED: USERS.errors.organisationNotAllowed,
  USER_ORGANISATION_HAS_NO_PERSON_FIELDS:
    USERS.errors.organisationHasNoPersonFields,
  USER_PLURAL_SALUTATION_NOT_ALLOWED: USERS.errors.pluralSalutationNotAllowed,
  USER_PLURAL_SALUTATION_HAS_NO_FIRST_NAME:
    USERS.errors.pluralSalutationHasNoFirstName,
  USER_SALUTATION_REQUIRED_WITHOUT_FIRST_NAME:
    USERS.errors.salutationRequiredWithoutFirstName,
  USER_NAME_OWNED_BY_HUBSPOT: USERS.errors.nameOwnedByHubSpot,
  USER_NOT_KNOWN_TO_HUBSPOT: USERS.errors.notKnownToHubSpot,
  USER_EMAIL_ALREADY_RECORDED: USERS.errors.emailAlreadyRecorded,
  USER_HUBSPOT_SYNC_CUSTOMERS_ONLY: USERS.errors.hubSpotSyncCustomersOnly,
  USER_CUSTOMER_NUMBER_TAKEN: p => USERS.errors.customerNumberTaken(p.number),
  USER_CUSTOMER_NUMBER_NOT_ALLOWED: USERS.errors.customerNumberNotAllowed,
  ELEMENT_TYPE_KEY_TAKEN: HOUSE_ELEMENT_TYPE.errors.keyTaken,
  ELEMENT_TYPE_STILL_USED: p =>
    HOUSE_ELEMENT_TYPE.errors.stillUsed(p.key, p.count),
  ELEMENT_TYPE_OPTIONS_STILL_USED: p =>
    HOUSE_ELEMENT_TYPE.errors.optionsStillUsed(p.keys),

  CONTRACT_PARTY_REQUIRED: FINANCE.errors.partyRequired,
  CONTRACT_PARTY_NOT_ALLOWED: FINANCE.errors.partyNotAllowed,
  CONTRACT_PARTY_STILL_USED: p => FINANCE.errors.partyStillUsed(p.count),
  CONTRACT_PARENT_MISMATCH: FINANCE.errors.parentMismatch,
  CONTRACT_HAS_INVOICES: p => FINANCE.errors.hasInvoices(p.count),
  CONTRACT_HAS_SUPPLEMENTS: p => FINANCE.errors.hasSupplements(p.count),
  CONTRACT_TEMPLATE_MISMATCH: FINANCE.errors.templateMismatch,
  DEDUCTION_PARTY_PER_INVOICE_ONLY: FINANCE.errors.deductionPartyPerInvoiceOnly,
  DEDUCTION_NEEDS_ONE_FIGURE: FINANCE.errors.deductionNeedsOneFigure,
  DEDUCTION_AMOUNT_OF_CONTRACT_ONLY:
    FINANCE.errors.deductionAmountOfContractOnly,
  MILESTONE_NOT_IN_PROJECT: FINANCE.errors.milestoneNotInProject,
  MILESTONE_HAS_INVOICED_INSTALLMENTS: p =>
    FINANCE.errors.milestoneHasInvoiced(p.count),
  MILESTONE_RELEASES_RETENTION: p =>
    FINANCE.errors.milestoneReleasesRetention(p.count),
  MILESTONE_CODE_TAKEN: p => FINANCE.errors.milestoneCodeTaken(p.code),
  INSTALLMENT_INVOICED: FINANCE.errors.installmentInvoiced,
  INSTALLMENT_NEEDS_ONE_FIGURE: FINANCE.errors.installmentNeedsOneFigure,
  INSTALLMENT_NOT_IN_CONTRACT: FINANCE.errors.installmentNotInContract,
  INVOICE_RETENTION_WITHOUT_CONTRACT: FINANCE.errors.retentionWithoutContract,
  INSTALLMENT_CREDIT_UNKNOWN: FINANCE.errors.installmentCreditUnknown,
  PLANNING_FEE_NOT_ALLOWED: FINANCE.errors.planningFeeNotAllowed,
  PLANNING_FEE_EXCEEDS_INSTALLMENT:
    FINANCE.errors.planningFeeExceedsInstallment,
  INSTALLMENT_CREDITED: FINANCE.errors.installmentCredited,
  INVOICE_CORRECTION_TARGET: FINANCE.errors.correctionTarget,
  INVOICE_HAS_CORRECTIONS: p => FINANCE.errors.invoiceHasCorrections(p.action),
  INSTALLMENT_HAS_DEDUCTION: p =>
    FINANCE.errors.installmentHasDeduction(p.title),
  INVOICE_CANCELLED: FINANCE.errors.invoiceCancelled,
  INVOICE_NOT_DELETABLE: FINANCE.errors.invoiceNotDeletable,
  INVOICE_HAS_PAYMENTS: p => FINANCE.errors.invoiceHasPayments(p.count),
  INVOICE_NOT_STAMPED: FINANCE.errors.invoiceNotStamped,
  ALLOCATION_SIGN_MISMATCH: FINANCE.errors.allocationSignMismatch,
  INVOICE_HAS_NO_DOCUMENT: FINANCE.errors.invoiceHasNoDocument,
  DOCUMENT_NOT_PDF: DOCUMENT_ERRORS.readOnlyPdf,
  DOCUMENT_TOO_LARGE: p => DOCUMENT_ERRORS.readSourceTooLarge(p.maxMb),
  DOCUMENT_READ_FAILED: p => DOCUMENT_ERRORS.readFailed(p.reason),
  ALLOCATION_EXCEEDS_INVOICE: p =>
    FINANCE.errors.allocationExceedsInvoice(
      formatEuro(p.allocated),
      formatEuro(p.issued),
    ),
  ALLOCATION_HAS_APPROVALS: FINANCE.errors.allocationHasApprovals,
  APPROVAL_EXCEEDS_LINE: p =>
    FINANCE.errors.approvalExceedsLine(formatEuro(p.left)),
  APPROVAL_DEDUCTS_MORE_THAN_APPROVED:
    FINANCE.errors.approvalDeductsMoreThanApproved,
  PAYMENT_EXCEEDS_OPEN: p =>
    FINANCE.errors.paymentExceedsOpen(formatEuro(p.open)),
  CONTRACT_DIRECT_FIXED: FINANCE.errors.directFixed,
  INVOICE_APPROVE_OVER_INSTALLMENT: FINANCE.errors.approveOverInstallment,
  INVOICE_APPROVE_LINE_CUT: FINANCE.errors.approveLineCut,
  INVOICE_MOVE_CORRECTION: FINANCE.errors.moveCorrection,
  INVOICE_MOVE_SAME_CONTRACT: FINANCE.errors.moveSameContract,
  INVOICE_MOVE_DIRECTION: FINANCE.errors.moveDirection,
  INVOICE_MOVE_RETENTION_RELEASE: FINANCE.errors.moveRetentionRelease,
  INVOICE_MOVE_RETENTION_HELD: FINANCE.errors.moveRetentionHeld,
  INVOICE_MOVE_CANCELLED_CONTRACT: FINANCE.errors.moveCancelledContract,
  INVOICE_MOVE_OVER_INSTALLMENT: FINANCE.errors.moveOverInstallment,
  INVOICE_RETENTION_EXCEEDED: p =>
    FINANCE.errors.retentionExceeded(
      formatEuro(p.invoicedBack),
      formatEuro(p.held),
    ),
  INVOICE_MOVE_BANK_PAYMENT: FINANCE.errors.moveBankPayment,
  INVOICE_MOVE_SHARED_PAYMENT: FINANCE.errors.moveSharedPayment,
  INVOICE_MOVE_LINE_UNKNOWN: FINANCE.errors.moveLineUnknown,
  BANK_NOT_CONFIGURED: BANK.errors.notConfigured,
  BANK_UNAVAILABLE: BANK.errors.unavailable,
  BANK_TRANSACTION_NOT_BOOKABLE: BANK.errors.notBookable,
  BANK_TRANSACTION_BOOKED: BANK.errors.booked,
  BANK_TRANSACTION_WRONG_WAY: BANK.errors.wrongWay,
  BANK_TRANSACTION_EXCEEDED: p => BANK.errors.exceeded(formatEuro(p.left)),
  BANK_AMOUNT_REQUIRED: BANK.errors.amountRequired,
  ROOM_CODE_TAKEN: p => ROOM_ERRORS.codeTaken(p.code),
  ROOM_CODE_ALLOCATION_FAILED: ROOM_ERRORS.codeAllocationFailed,
  FLOOR_NOT_IN_PROPERTY: ROOM_ERRORS.floorNotInProperty,
  SYNC_ALREADY_RUNNING: p => DEVELOPMENT.sync.errors.alreadyRunning(p.live),
  BACKUP_ALREADY_RUNNING: p => BACKUP_ERRORS.alreadyRunning(p.live),
  CUSTOMER_UNSUBSCRIBED: PROJECTS.notifyCustomer.errors.unsubscribed,
  EMAIL_SEND_FAILED: PROJECTS.notifyCustomer.errors.sendFailed,

  POSITION_ADJUSTMENT_INCOMPLETE: COST_ENTRY.price.errors.incomplete,
  POSITION_DISCOUNT_OUT_OF_RANGE: COST_ENTRY.price.errors.discountRange,
  POSITION_HAS_NO_LIVE_PRICE: COST_ENTRY.price.errors.noLivePrice,
  POSITION_IS_A_PICK: COST_ENTRY.price.errors.pickIsOwned,
  POSITION_TEXT_UNCHANGED: COST_ENTRY.serviceDescription.errors.unchanged,
  POSITION_TEXT_IS_THE_CATALOGUE_S:
    COST_ENTRY.serviceDescription.errors.fromCatalogue,
  // The refusal that is a question: the screen renders the projects under it
  // and the agent reads the same sentence back to its user.
  POSITIONS_WOULD_BE_ORPHANED: p =>
    COST_ENTRY.orphan.confirmMessage(p.count, p.projects.length),
  MEASURE_TEXT_WOULD_DRIFT: p =>
    CATALOGUE.measure.textDrift.message(p.count, p.projects.length),
  POSITION_PICK_NOT_DELETABLE: COST_ENTRY.delete.isPick,
  POSITION_IS_OWN_LINE: COST_ENTRY.price.errors.ownLine,
  POSITION_LOCKED: CONFIGURATOR.locked.serverError,
  POSITION_PRICE_NEGATIVE: COST_ENTRY.price.errors.negative,

  COMMENT_IS_A_RECORD: COMMENTS.errors.isARecord,

  NOT_FOUND: COMMON.errors.notFound,
  FORBIDDEN: COMMON.errors.forbidden,
  INVALID_CREDENTIALS: AUTH.loginFailed,
} as const satisfies {
  [C in ServerErrorCode]: string | ((params: ServerErrorParams[C]) => string)
}

/**
 * Resolve a code that arrived over the wire, where it is only a string. Returns
 * `undefined` for anything not in the table so the caller can fall back rather
 * than show the user a raw code.
 */
export function serverErrorText(
  code: string,
  params: Record<string, unknown>,
): string | undefined {
  // The code arrives over the wire as a plain string, so this is where the
  // typed contract ends. It holds at the throw site instead: `codedBadRequest`
  // takes `ServerErrorParams[code]`, so the parameter names cannot drift from
  // the ones interpolated above.
  const text = SERVER_ERROR_TEXT[code as ServerErrorCode] as
    | string
    | ((p: Record<string, unknown>) => string)
    | undefined
  return typeof text === 'function' ? text(params) : text
}

/**
 * A coded error body as the sentence a person reads, or undefined where the
 * body carries no code this side knows.
 *
 * The envelope's own four fields are what `codedBadRequest` and its siblings
 * put around a code, and they are not parameters: one reaching a message
 * function would be interpolated as though it were. Stated here because this
 * is where the code and its parameters are read — both the HTTP path and a
 * worker's failure go through it, and a fifth envelope field added to the
 * producer then has one place to be taught, not two.
 */
export function codedErrorText(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null) return undefined

  const {
    statusCode: _status,
    error: _error,
    message: _message,
    code,
    ...params
  } = body as { code?: unknown } & Record<string, unknown>

  return typeof code === 'string' ? serverErrorText(code, params) : undefined
}
